import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Pagination } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../Commonlibs/userSlice';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
// Import assets
import bedIcon from '../../../assets/squarefeet.png';
import bathIcon from '../../../assets/baths.png';
import areaIcon from '../../../assets/feet.png';
import saveIcon from '../../../assets/savep.png';
import shareIcon from '../../../assets/sharep.png';
// Import loader
import Loader from '../../../Loader/Loader';

const ProfileViewed = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const itemsPerPage = 5; // Show 5 properties per page

  const navigate = useNavigate();
  const user = useSelector(selectUser);
  useEffect(() => {
    const fetchViewedProperties = async () => {
      try {
        setLoading(true);
        const buyer_id = user?.id // Fallback to 2 for testing as shown in your example
       
        const response = await axios.get(
          `https://propertydealstalk.com/api/buyerAnalytics/getBuyerDashboardPropertiesByType?buyer_id=${buyer_id}&type=recently_viewed`
        );
       
        if (response.data.statusCode === 200 && response.data.status === "SUCCESS") {
          setProperties(response.data.result);
          // Calculate total pages
          setTotalPages(Math.ceil(response.data.result.length / itemsPerPage));
        } else {
          setError(response.data.message || "Failed to fetch properties");
        }
      } catch (err) {
        setError("Error fetching viewed properties");
        console.error("Error fetching viewed properties:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchViewedProperties();
  }, [user]);

  // Get current page items
  const getCurrentPageItems = () => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return properties.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to format price with commas
  const formatPrice = (price) => {
    if (!price) return '0';
    return '₹' + price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Function to get property image or placeholder
  const getPropertyImage = (property) => {
    if (!property.image) {
      return 'https://via.placeholder.com/300x140?text=No+Image';
    }

    // Check if the image is a base64 string
    if (property.image.startsWith('data:image/')) {
      // If it's already a proper data URL, return it as is
      return property.image;
    } else if (property.image.startsWith('/')) {
      // If it's a base64 string without the data URL prefix
      // Check if it's already a complete base64 string
      if (property.image.includes('base64,')) {
        return property.image;
      } else {
        // Assume it's a base64 string and add the proper prefix
        return `data:image/jpeg;base64,${property.image}`;
      }
    } else {
      // If it's a full URL
      return property.image;
    }
  };

  // Function to handle navigation to property details
  const handlePropertyClick = (propertyId) => {
    navigate(`/view/${propertyId}`);
  };

  if (loading) return <Loader />;

  return (
    <Box
      sx={{
        background: '#fff',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '1100px',
        p: { xs: '16px', md: '24px' },
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        mb: '24px',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
    >
      {error ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : properties.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography>No recently viewed properties found.</Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '30px',
            }}
          >
            {getCurrentPageItems().map((property) => (
              <Box
                key={property.id}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  background: '#fff',
                  border: '1px solid #ddd',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  height: { md: '140px', xs: 'auto' },
                  width: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  },
                }}
                onClick={() => handlePropertyClick(property.id)}
              >
                <Box
                  component="img"
                  src={getPropertyImage(property)}
                  alt={property.property_name || 'Property Image'}
                  sx={{
                    width: { md: '300px', xs: '100%' },
                    height: { md: '140px', xs: '180px' },
                    objectFit: 'cover',
                    flexShrink: 0,
                    borderTopLeftRadius: { md: '8px', xs: 0 },
                    borderBottomLeftRadius: { md: '8px', xs: 0 },
                  }}
                />
                <Box
                  sx={{
                    flex: 1,
                    ml: { md: '20px', xs: 0 },
                    p: { xs: '16px', md: '10px' },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Box>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>
                        {property.property_name || 'Unnamed Property'}
                      </h3>
                      <p
                        style={{
                          color: '#2563EB',
                          fontWeight: 600,
                          margin: '5px 0',
                        }}
                      >
                        {formatPrice(property.asking_price)}
                      </p>
                      <p
                        style={{
                          color: '#555',
                          fontSize: '14px',
                          marginBottom: '11px',
                        }}
                      >
                        {property.area_name || ''}{property.area_name && property.location ? ', ' : ''}{property.location || ''}
                      </p>
                      <p
                        style={{
                          color: '#555',
                          fontSize: '14px',
                          marginTop: 0,
                        }}
                      >
                        {property.property_type === 'residential' ? 'Residential' : 'Commercial'} {property.asset_type || ''}
                      </p>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: '10px',
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                      }}
                      onClick={(e) => e.stopPropagation()} // Prevent navigation when clicking buttons
                    >
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'none',
                          border: 'none',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        <img
                          src={saveIcon}
                          alt="Save"
                          style={{ width: '98px', height: '36px' }}
                        />
                      </button>
                      <button
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          background: 'none',
                          border: 'none',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        <img
                          src={shareIcon}
                          alt="Share"
                          style={{ width: '98px', height: '36px' }}
                        />
                      </button>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: { md: '120px', xs: '12px' },
                      flexDirection: { xs: 'column', md: 'row' },
                      flexWrap: { xs: 'wrap', md: 'nowrap' },
                      mt: { md: '5px', xs: '10px' },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {/* <img src={bedIcon} alt="Beds" /> */}
                      <span>{property.bhk || '-'} BHK</span>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {/* <img src={bathIcon} alt="Baths" /> */}
                      <span>{property.furnish_type || '-'}</span>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      {/* <img src={areaIcon} alt="Area" /> */}
                      <span>{property.property_size || '-'} {property.property_size_units || 'sq.ft'}</span>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
         
          {/* Pagination component - only show if we have more than one page */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#333',
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#2563EB !important',
                    color: 'white',
                  }
                }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ProfileViewed;